/**
 * Контрольные суммы ИНН и ОГРН, формат КПП — ЕДИНСТВЕННАЯ реализация в портале.
 *
 * Модуль лежит в контрактах, а не в API, потому что одну и ту же арифметику
 * обязаны считать три места: вход справочника контрагентов (§3.2), обезличиватель
 * закрытого корпуса (§1.4) и движок правил (§9.2). Пока копий было две — в
 * `apps/api/src/lib/validators.ts` и в обезличивателе — они совпадали до цифры, и
 * именно это делало их опасными: расхождение оказалось бы МОЛЧАЛИВЫМ. §1.4
 * требует, чтобы обезличиватель сохранял валидность или намеренную невалидность
 * реквизита ровно так, как её понимает портал; разойдись веса на один знак —
 * «битый ОГРН» производного корпуса стал бы валидным для проверок, и
 * регрессионный тест на этот дефект перестал бы проверять хоть что-нибудь, не
 * покраснев ни разу.
 *
 * Поэтому наружу выдаётся не только ПРОВЕРКА значения, но и ПОРОЖДЕНИЕ
 * контрольных цифр (`innControlDigits`, `ogrnControlDigit`): обезличивателю нужно
 * строить синтетические значения с верной либо намеренно неверной суммой, и если
 * бы он считал цифру сам, веса и модули снова лежали бы в двух местах. Проверка
 * здесь сама построена на этих же функциях, так что «сошлись» они не по
 * дисциплине автора, а структурно.
 *
 * ## Отказ называет ПРИЧИНУ, а не только факт
 *
 * Веса и модули проверены на реальных значениях корпуса: десять ИНН из актов
 * проходят, пять ОГРН из семи проходят, два не проходят — и это ДВА РАЗНЫХ
 * случая, ради различения которых функции возвращают причину отказа, а не
 * `boolean`:
 *
 * | Класс из корпуса           | Причина             | Что это          | Правило S9     |
 * | -------------------------- | ------------------- | ---------------- | -------------- |
 * | 12 цифр вместо 13          | `length` (12 из 13) | дефект документа | `fail`         |
 * | 13 цифр, сумма не сходится | `checksum`          | дефект OCR       | `undetermined` |
 *
 * Сами значения лежат в `docs/CORPUS_FINDINGS.md` и в исходники не переносятся:
 * реквизиты участников здесь не нужны, и это держит `pnpm pii:scan`. Второе из
 * них вычитано с круглой печати, перекрытой подписью, где тот же OCR прочитал
 * слово «ОГРН» как «ОТРИ». Правило, поднявшее бы здесь `error`, обвинило бы
 * подрядчика в подделке реквизитов. Поэтому «не та длина» и «сумма не сходится»
 * обязаны быть различимы вызывающим кодом, а не сливаться в одно «невалидно».
 *
 * Значение НЕ нормализуется: пробелы, дефисы и неразрывные пробелы дают
 * `non_digits`, а не молча удаляются. Решение «здесь пробел лишний» принимает
 * нормализация извлечённого реквизита, которая видит контекст страницы; проверка
 * контрольной суммы такого контекста не имеет и додумывать за неё не должна.
 */
/** Веса контрольной цифры ИНН организации: тело — 9 цифр. */
const INN_10_WEIGHTS = [2, 4, 10, 3, 5, 9, 4, 6, 8];
/** Веса первой контрольной цифры ИНН физического лица: тело — 10 цифр. */
const INN_12_FIRST_WEIGHTS = [7, 2, 4, 10, 3, 5, 9, 4, 6, 8];
/** Веса второй: считается по телу ВМЕСТЕ с первой контрольной цифрой — 11 цифр. */
const INN_12_SECOND_WEIGHTS = [3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8];
/**
 * Делитель контрольной цифры ОГРН по длине его тела.
 *
 * У 13-значного ОГРН это 11, у 15-значного ОГРНИП — 13. Смешать их нельзя в обе
 * стороны: правило 13-значного, применённое к ОГРНИП, принимает подделанный
 * последний знак и отвергает настоящий реквизит.
 */
const OGRN_MODULUS = { 12: 11n, 14: 13n };
const ONLY_DIGITS = /^[0-9]+$/;
const ACCEPTED = { ok: true };
// =====================================================================
// Проверка значения
// =====================================================================
/**
 * ИНН: 10 цифр у организации, 12 у физического лица и ИП.
 *
 * У 12-значного две контрольные цифры, и обе обязательны: значение, прошедшее
 * первую и не прошедшее вторую, отвергается — иначе одна OCR-ошибка в последнем
 * знаке проходила бы насквозь. Цифры сравниваются по очереди, а не парой: в
 * замечании обязана стоять та пара «ожидалась / указана», на которой значение
 * действительно разошлось.
 */
export function checkInn(value) {
    const shape = checkShape(value, [10, 12]);
    if (shape !== null)
        return shape;
    if (value.length === 10) {
        return compareControlDigits(innControlDigits(value.slice(0, 9)), value.slice(9));
    }
    const control = innControlDigits(value.slice(0, 10));
    const first = compareControlDigits(control.slice(0, 1), value.slice(10, 11));
    if (!first.ok)
        return first;
    return compareControlDigits(control.slice(1), value.slice(11));
}
/**
 * ОГРН: 13 цифр у организации, 15 у ИП (ОГРНИП).
 */
export function checkOgrn(value) {
    const shape = checkShape(value, [13, 15]);
    if (shape !== null)
        return shape;
    const bodyLength = value.length - 1;
    return compareControlDigits(ogrnControlDigit(value.slice(0, bodyLength)), value.slice(bodyLength));
}
/**
 * КПП: девять цифр.
 *
 * Контрольной суммы у КПП не существует, поэтому `checkKpp` никогда не вернёт
 * `checksum` — проверить можно только форму. Латинские буквы в 5–6 знаке (код
 * причины постановки на учёт у иностранных организаций) не принимаются
 * намеренно: ровно так же ограничивает значение CHECK
 * `counterparties_kpp_chk`, и расхождение схемы с БД дало бы 500 вместо
 * внятного отказа на границе.
 */
export function checkKpp(value) {
    const shape = checkShape(value, [9]);
    return shape ?? ACCEPTED;
}
// =====================================================================
// Порождение контрольных цифр
// =====================================================================
/**
 * Контрольные цифры ИНН по его телу: 9 цифр тела дают одну, 10 цифр — две.
 *
 * Возвращается строка, а не число: у 12-значного ИНН контрольных цифр две, и
 * «06» — это не 6.
 *
 * Тело неверной длины или с посторонними символами — ошибка вызывающего, а не
 * значение с плохой суммой, поэтому здесь исключение, а не отказ и не ноль.
 * Молчаливый ноль превратил бы рассогласование в ЛОЖНОЕ подтверждение реквизита:
 * проверка сказала бы «сумма сходится» о значении, которого не считала.
 */
export function innControlDigits(body) {
    requireDigits(body, 'ИНН');
    if (body.length === 9)
        return weightedDigit(body, INN_10_WEIGHTS);
    if (body.length === 10) {
        const eleventh = weightedDigit(body, INN_12_FIRST_WEIGHTS);
        return eleventh + weightedDigit(body + eleventh, INN_12_SECOND_WEIGHTS);
    }
    throw new RangeError(`Тело ИНН — 9 или 10 цифр, получено ${body.length}`);
}
/**
 * Контрольная цифра ОГРН по его телу: 12 цифр (ОГРН) или 14 (ОГРНИП).
 *
 * Остаток от деления числа, составленного из всех цифр кроме последней, на 11
 * либо 13, взятый по модулю 10. Делимое собирается через `BigInt`: остаток тогда
 * точен при любой длине и не зависит от границы 2^53, за которой целые числа в
 * `Number` перестают быть представимыми. Тело ОГРНИП (14 цифр) в `Number` ещё
 * влезает, но выбор сделан на длину вперёд — внутренние коды и реквизиты ЕАЭС
 * бывают длиннее.
 */
export function ogrnControlDigit(body) {
    requireDigits(body, 'ОГРН');
    const modulus = OGRN_MODULUS[body.length];
    if (modulus === undefined) {
        throw new RangeError(`Тело ОГРН — 12 или 14 цифр, получено ${body.length}`);
    }
    return ((BigInt(body) % modulus) % 10n).toString();
}
// =====================================================================
// Внутреннее
// =====================================================================
/** Пустота, посторонние символы, длина. `null` — форма пригодна. */
function checkShape(value, allowedLengths) {
    if (value.length === 0)
        return rejected('empty');
    if (!ONLY_DIGITS.test(value))
        return rejected('non_digits');
    if (!allowedLengths.includes(value.length))
        return rejected('length');
    return null;
}
function rejected(defect) {
    return { ok: false, defect, checksum: null };
}
function requireDigits(body, label) {
    if (!ONLY_DIGITS.test(body)) {
        throw new TypeError(`Тело ${label} состоит только из цифр, получено «${body}»`);
    }
}
/**
 * Взвешенная сумма по модулю 11, затем по модулю 10.
 *
 * Число весов равно числу читаемых цифр — это держат проверки длины у обеих
 * порождающих функций, поэтому выхода за строку здесь быть не может.
 */
function weightedDigit(digits, weights) {
    const sum = weights.reduce((total, weight, index) => total + weight * (digits.charCodeAt(index) - 48), 0);
    return String((sum % 11) % 10);
}
function compareControlDigits(expected, actual) {
    if (expected === actual)
        return ACCEPTED;
    return { ok: false, defect: 'checksum', checksum: { expected, actual } };
}
/** Тире всех начертаний, которые OCR ставит вместо дефиса. */
const DOC_NO_DASHES = /[\u2010\u2011\u2012\u2013\u2014\u2015\u2212\uFE58\uFE63\uFF0D]/gu;
/**
 * Символы, выбрасываемые из номера.
 *
 * Точки и слэши НЕ выбрасываются: в `РОСС RU Д-RU.PA01.B.17254/23` и
 * `230126/2/126000477.1.1` они разделяют значащие поля номера, и без них
 * разные номера схлопнулись бы в один.
 *
 * Точка с запятой, наоборот, выбрасывается, и это не косметика. OCR ставит `;`
 * на месте ПЕРЕНОСА СТРОКИ внутри ячейки — тот же след распознавания, который
 * уже учтён в `normalizeRegistryName` и в разборе дат («от; 24.01.2026»).
 * В номере он даёт `RU.MCC.240;.445.38406` там, где напечатано
 * `RU.MCC.240.445.38406`, и строка реестра получает `missing` при документе,
 * который в комплекте лежит. Значащей частью номера `;` не бывает: это
 * разделитель предложений, а не полей реквизита.
 */
const DOC_NO_DROPPED = /[\s;\u00A0\u00AD\u2116\u0022\u0027\u0060\u00AB\u00BB\u201C\u201D\u201E\u2018\u2019]/gu;
/**
 * Приставка «номер» в НАЧАЛЕ значения — вместе с тем, во что её превращает OCR.
 *
 * Сам знак `№` уже выброшен `DOC_NO_DROPPED`, но на боевом комплекте он
 * прочитался БУКВАМИ: 35 из 138 строк перечней приложений пришли как
 * `Не 6846`, `Не RU.СМИК.001.Н.00270`, `No 123`. В нормализованный номер
 * уезжало `НЕ6846`, и строка реестра не сходилась с документом `6846` — то
 * есть портал объявлял отсутствующим документ, который в комплекте лежит.
 *
 * Срезается только ПРИСТАВКА и только когда за ней стоит остаток номера.
 * Условие продолжения у форм разное, и это не небрежность: кириллическое `НЕ`
 * возникает лишь как ошибка чтения `№`, поэтому за ним годится и буква
 * (`Не RU.СМИК…`), а латинские `NO`/`N` — законное начало номера сами по себе,
 * и срезаются только перед цифрой. Формы перечислены закрытым списком, а не
 * «двумя буквами в начале»: `НД` — обозначение нормативного документа, и
 * снести его значило бы придумать номер.
 */
const DOC_NO_NUMBER_PREFIX = /^(?:НЕ(?=[0-9A-ZА-Я])|NO(?=[0-9])|N(?=[0-9]))/u;
/**
 * Пунктуация в ХВОСТЕ номера: она принадлежит предложению, а не реквизиту.
 *
 * Внутренняя точка значаща и не трогается — `RU.MCC.240.445.38406` без неё
 * схлопнулся бы в другой номер. Хвостовая не значаща никогда: она появляется
 * там, где номер стоит в конце фразы. На листе исполнительной схемы напечатано
 * «…реквизитами исполнительной схемы № 001.», и точка попадала в реквизит —
 * после чего строка реестра «ИС №001» не находила документ ни точным
 * сравнением, ни фолдингом, ни вхождением куска.
 *
 * Снимается только в сравнимых формах. `raw` остаётся дословным: он
 * показывается человеку и обязан совпадать с тем, что напечатано на листе.
 */
const DOC_NO_TRAILING = /[.,;:!?]+$/u;
/**
 * Кириллические буквы, неотличимые от латинских в скане, и их латинские пары.
 *
 * Записаны кодами намеренно: в исходнике `С` и `C` выглядят одинаково, и
 * таблица, набранная буквами, не поддаётся вычитке глазами — ошибка в ней
 * была бы невидимой ровно тем же способом, каким она невидима в самом OCR.
 */
const CYRILLIC_LOOKALIKES = '\u0410\u0412\u0415\u041A\u041C\u041D\u041E\u0420\u0421\u0422\u0425\u0423';
const LATIN_EQUIVALENTS = 'ABEKMHOPCTXU';
/**
 * Буквы, которые в НОМЕРЕ пишут то кириллицей, то латинской транслитерацией.
 *
 * Это не гомоглифы: «И» и «I» в скане различимы. Смешение происходит не в
 * глазах OCR, а в руках — аббревиатуру органа по сертификации набирают обеими
 * раскладками. Сертификат КНАУФ в папке «ИД Мастер апрель 2026» напечатан как
 * `RU.СМИК.001.Н.00270`, а опись передачи называет его то так же, то
 * `RU.CMIK.001.H.00270`: латиницей набраны строки 1.4–7.4, кириллицей —
 * 8.4–10.4, в ОДНОЙ И ТОЙ ЖЕ описи. Буквы `С`, `М`, `К`, `Н` складывались
 * таблицей выше, а `И` — нет, и восемь строк уходили на последнюю ступень
 * лестницы («допуск в один знак», счёт 0.5) с подписью «номер совпал не
 * полностью — проверьте документ», то есть человека звали туда, где сверять
 * нечего.
 *
 * Пара записана отдельной таблицей, а не дописана к гомоглифам: у неё другая
 * причина, и смешивать их в одном списке значило бы потерять довод. Плата та
 * же и ограничена тем же: совпадение по `folded` никогда не равно точному
 * (§8.3), при нескольких кандидатах строка становится `ambiguous`, а не
 * `matched`.
 */
const CYRILLIC_TRANSLITERATED = '\u0418';
const LATIN_TRANSLITERATIONS = 'I';
/**
 * Таблица фолдинга: символ → канонический представитель класса.
 *
 * Помимо десяти пар §8.3 (С А Р О Е М Т Х В К) сюда входят ещё две, без
 * которых не сходится ЭТАЛОННЫЙ ПРИМЕР самого §8.3 — одна и та же декларация
 * `РОСС RU Д-RU.PA01.B.17254/23` и `РОСС RU Д-РУ.РА01.В.17254/23`:
 *
 * - `У → U`. В первой форме стоит латинская `U` (U+0055), во второй —
 *   кириллическая `У` (U+0423). Без пары номера не сходятся.
 * - `R → P`. Здесь важна причина. В оригинале напечатано латинское `RU`;
 *   во второй форме OCR прочитал `R` как кириллическую `Р`, а `Р` — гомоглиф
 *   латинской `P`, а не `R`. То есть в реальном тексте `R`, `Р` и `P` — один
 *   класс смешения, и разбить его нельзя: `РУ → PU`, а `RU` осталось бы `RU`.
 *
 * Третьей парой добавлен НОЛЬ: `О → 0` и `O → 0`, то есть класс `{О, O, 0}`.
 * Кириллическая и латинская «о» уже были одним классом, а нуль от них
 * неотличим в сканах ровно так же — и это не догадка. Приложение к сертификату
 * пожарной безопасности АРТАЛИКС в папке «ИД Мастер апрель 2026» ссылается на
 * родителя `РОСС RU.32311.0С01.ПБ01.0539` (нуль), тогда как на самом
 * сертификате напечатано `…ОС01…` (буква). Пять приложений из шести не нашли
 * своего документа: страницы остались неотнесёнными, а строки реестра —
 * «нет в комплекте».
 *
 * Направление важно: складываем В НОЛЬ, а не в букву. Обратное сложение
 * разорвало бы цифровые серии («0539» стало бы «O539»), а на них держится
 * ступень сверки по числовому ядру.
 *
 * Класс `{R, Р, P}` шире собственно гомоглифов, и это осознанная плата.
 * Безопасной её делает §8.3: совпадение по `folded` НИКОГДА не считается
 * равным точному — при единственном кандидате оно даёт score < 1 и пометку
 * в объяснении, при нескольких — `ambiguous`, а не `matched`. Обратное —
 * сузить фолдинг до «чистых» гомоглифов — означало бы `missing` на документе,
 * который в комплекте лежит: замер `pg_trgm.similarity` на этой же паре дал
 * 0.55, то есть порогами similarity расхождение не закрывается
 * (`docs/CORPUS_FINDINGS.md`).
 */
const HOMOGLYPH_FOLD = new Map([
    ...[...CYRILLIC_LOOKALIKES].map((cyrillic, index) => [
        cyrillic,
        LATIN_EQUIVALENTS[index],
    ]),
    ...[...CYRILLIC_TRANSLITERATED].map((cyrillic, index) => [
        cyrillic,
        LATIN_TRANSLITERATIONS[index],
    ]),
    ['R', 'P'],
    // Идут ПОСЛЕ таблицы пар: `О → O` из неё перекрывается здесь, иначе класс
    // распался бы надвое — буквы отдельно, нуль отдельно.
    ['\u041E', '0'],
    ['\u004F', '0'],
]);
/**
 * Три сравнимые формы номера документа (§8.3).
 *
 * Функция живёт в контрактах, а не в разборе реестра, по причине S4: одну и
 * ту же нормализацию применяют разбор реестра, сверка комплекта и (с S9)
 * правила `REG.*`. Разойдись копии — часть строк реестра молча перестала бы
 * находить свои документы, и выглядело бы это как неполный комплект, а не
 * как дефект кода.
 *
 * Значение не разбирается на поля и не проверяется на осмысленность: номер
 * документа не имеет единой формы, и попытка «понять» его здесь означала бы
 * отбрасывание всего, чего автор не предвидел.
 */
export function normalizeDocNo(input) {
    const normalized = input
        .toUpperCase()
        .replace(DOC_NO_DASHES, '-')
        .replace(DOC_NO_DROPPED, '')
        .replace(DOC_NO_TRAILING, '')
        // ПОСЛЕ выброса пробелов и `№`: приставка к этому моменту склеена с
        // остатком номера, и распознать её можно только здесь.
        .replace(DOC_NO_NUMBER_PREFIX, '');
    let folded = '';
    for (const char of normalized) {
        folded += HOMOGLYPH_FOLD.get(char) ?? char;
    }
    return { raw: input, normalized, folded, compact: compactOf(normalized) };
}
/** Знак, которым в компактной форме отмечена граница цифровых групп. */
const COMPACT_DIGIT_BOUNDARY = '·';
/**
 * Компактная форма: разделители выброшены, граница цифровых групп сохранена.
 *
 * Граница считается по ТОЧНОЙ форме, а фолдинг применяется после. Иначе
 * решение о границе принимал бы уже свёрнутый текст, где буква «О» стала нулём:
 * в «52-ОТ/1» после фолдинга стоят «2», разделитель и «0», и правило видело бы
 * две цифровые группы там, где напечатаны цифра и буква.
 */
function compactOf(normalized) {
    let result = '';
    let previousDigit = false;
    let pendingBoundary = false;
    for (const char of normalized) {
        if (!/[\p{L}\p{N}]/u.test(char)) {
            if (result !== '')
                pendingBoundary = true;
            continue;
        }
        const digit = /\d/u.test(char);
        // Граница ставится только между цифрами: «52-ОТ/1» и «52ОТ1» — одно и то
        // же, а «1.23» и «12.3» — разные номера двух разных актов.
        if (pendingBoundary && digit && previousDigit)
            result += COMPACT_DIGIT_BOUNDARY;
        result += HOMOGLYPH_FOLD.get(char) ?? char;
        previousDigit = digit;
        pendingBoundary = false;
    }
    return result;
}
//# sourceMappingURL=identifiers.js.map